import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { constants, PrimaryButton, SecondaryButton } from '../index';
import {
  OutlinedTextField,
  DatePickerField,
  TaxIdField,
  PhoneNumberField,
  ZipCodeField,
} from '../fields';
import TagsDropdown from './TagsDropdown';
import { AddFamilyFormProps } from './types';

export default function AddFamilyForm({
  formState,
  setFormState,
  primaryMemberTags,
  handleChange,
  errors,
}: AddFamilyFormProps) {
  return (
    <Stack>
      <Stack direction="row">
        <OutlinedTextField
          select
          id="relationship-type"
          name="relationship"
          label="Relationship"
          value={formState.relationship}
          onChange={handleChange}
          error={!!errors.relationship}
          helperText={errors.relationship}
        >
          <MenuItem key="relationship-spouse" value={constants.RELATIONSHIP_SPOUSE}>
            {constants.RELATIONSHIP_LABEL_SPOUSE}
          </MenuItem>
          <MenuItem key="relationship-dependent" value={constants.RELATIONSHIP_DEPENDENT}>
            {constants.RELATIONSHIP_LABEL_DEPENDENT}
          </MenuItem>
          <MenuItem
            key="relationship-dependent-adult"
            value={constants.RELATIONSHIP_DEPENDENT_ADULT}
          >
            {constants.RELATIONSHIP_LABEL_DEPENDENT_ADULT}
          </MenuItem>
          <MenuItem
            key="relationship-dependent-student"
            value={constants.RELATIONSHIP_DEPENDENT_STUDENT}
          >
            {constants.RELATIONSHIP_LABEL_DEPENDENT_STUDENT}
          </MenuItem>
        </OutlinedTextField>
        <DatePickerField
          id="employee-dob"
          name="dob"
          label="Date of Birth"
          onChange={handleChange}
          value={formState.dob}
          helperText={errors.dob}
          error={!!errors.dob}
        />
      </Stack>
      <Stack direction="row">
        <OutlinedTextField
          id="employee-first-name"
          name="identificationType"
          label="Identification Type"
          onChange={handleChange}
          value={formState.identificationType}
          helperText={errors.identificationType}
          error={!!errors.identificationType}
          select
        >
          {constants.TAX_ID_MEMBER_OPTIONS.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </OutlinedTextField>
        <TaxIdField
          id="employee-ssn"
          name="identificationId"
          label="Identificaton #"
          onChange={handleChange}
          value={formState.identificationId}
          type={formState.identificationType}
          helperText={errors.identificationId}
          error={!!errors.identificationId}
          disabled={formState.identificationType === constants.TAX_ID_TYPE_MEMBER_NONE}
        />
      </Stack>
      <Stack direction="row">
        <OutlinedTextField
          id="employee-first-name"
          name="firstName"
          label="First Name"
          onChange={handleChange}
          value={formState.firstName}
          helperText={errors.firstName}
          error={!!errors.firstName}
        />
        <OutlinedTextField
          id="employee-middle-name"
          name="middleName"
          label="Middle Name"
          onChange={handleChange}
          value={formState.middleName}
          helperText={errors.middleName}
          error={!!errors.middleName}
        />
        <OutlinedTextField
          id="employee-last-name"
          name="lastName"
          label="Last Name"
          onChange={handleChange}
          value={formState.lastName}
          helperText={errors.lastName}
          error={!!errors.lastName}
        />
      </Stack>
      <PhoneNumberField
        id="employee-phone"
        name="phone"
        label="Phone"
        onChange={handleChange}
        value={formState.phone}
        helperText={errors.phone}
        error={!!errors.phone}
      />
      <OutlinedTextField
        id="employee-email"
        name="email"
        label="Email"
        onChange={handleChange}
        value={formState.email}
        helperText={errors.email}
        error={!!errors.email}
      />
      <TagsDropdown
        formState={formState}
        setFormState={setFormState}
        initialTags={formState.memberTags}
      />
      <Button
        variant="outlined"
        color="primary"
        id="btn-copy-tags"
        onClick={() => setFormState({ ...formState, memberTags: primaryMemberTags })}
        disabled={!primaryMemberTags}
      >
        Copy division codes from primary member
      </Button>
      <FormControlLabel
        id="use-same-address-primary"
        control={
          <Checkbox
            name="useSameAddress"
            checked={formState.useSameAddress}
            onChange={handleChange}
          />
        }
        label="Use same address as employee"
      />
      <OutlinedTextField
        id="employee-address1"
        name="address1"
        label="Address 1"
        onChange={handleChange}
        value={formState.address1}
        helperText={errors.address1}
        error={!!errors.address1}
        disabled={formState.useSameAddress}
      />
      <OutlinedTextField
        id="employee-address2"
        name="address2"
        label="Address 2"
        onChange={handleChange}
        value={formState.address2}
        helperText={errors.address2}
        error={!!errors.address2}
        disabled={formState.useSameAddress}
      />
      <Stack direction="row">
        <OutlinedTextField
          id="employee-address1"
          name="city"
          label="City"
          onChange={handleChange}
          value={formState.city}
          helperText={errors.city}
          error={!!errors.city}
          disabled={formState.useSameAddress}
        />
        <OutlinedTextField
          select
          label="State"
          id="state"
          name="state"
          value={formState.state}
          onChange={handleChange}
          error={!!errors.state}
          helperText={errors.state}
          disabled={formState.useSameAddress}
        >
          {Object.entries(constants.US_STATES).map(([stateAbbrev, state]) => (
            <MenuItem key={stateAbbrev} value={stateAbbrev}>
              {state}
            </MenuItem>
          ))}
        </OutlinedTextField>
        <ZipCodeField
          id="employee-address2"
          name="zip"
          label="Zip code"
          onChange={handleChange}
          value={formState.zip}
          helperText={errors.zip}
          error={!!errors.zip}
          disabled={formState.useSameAddress}
        />
      </Stack>
    </Stack>
  );
}
